import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, imageUrl, videoUrl, DOMAINS, PRIVACY_LEVELS, NATURE_TYPES } from "@/lib/api";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import {
  ArrowLeft, MapPin, Camera, Tag, PencilSimple, Broadcast, ArrowsClockwise,
  Trash, Leaf, TextT, Buildings, Users, Plus, X, Info, CheckCircle, Crop,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import CropModal from "@/components/CropModal";

const RichText = ({ html, className }) => (
  <div className={`rich-html ${className || ""}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || "") }} />
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
    <span className="overline text-[10px] text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium text-zinc-800">{value ?? "—"}</span>
  </div>
);

export default function PhotoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [edit, setEdit] = useState({ title: "", author: "", photographer: "", photographer_url: "", caption: "", description: "", primary_domain: "", landmark_name: "", city: "", country: "" });
  const [nat, setNat] = useState({ common_name: "", scientific_name: "", type: "plant" });
  const [cat, setCat] = useState({ category: "", subcategory: "" });

  const load = () => api.get(`/photos/${id}`).then((r) => setP(r.data)).catch(() => nav("/library"));
  useEffect(() => { load(); const t = setInterval(() => { if (p?.status === "analyzing") load(); }, 4000); return () => clearInterval(t); }, [id, p?.status]);

  const [cropFile, setCropFile] = useState(null);
  const [imgVer, setImgVer] = useState(0);
  const openCrop = async () => {
    try {
      const res = await api.get(`/photos/${id}/image`, { responseType: "blob" });
      setCropFile(new File([res.data], "photo.jpg", { type: res.data.type || "image/jpeg" }));
    } catch (e) { toast.error("Could not load image for cropping"); }
  };
  const saveCrop = async (file) => {
    setCropFile(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/photos/${id}/crop`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo cropped");
      setImgVer((v) => v + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Crop failed"); }
  };

  if (!p) return <div className="grid h-64 place-items-center text-muted-foreground">Loading…</div>;

  const orig = p.original || {};
  const loc = p.location || {};
  const ai = p.ai || {};
  const tags = p.manual_tags || [];

  const addTag = async () => {
    const v = tagInput.trim();
    if (!v) return;
    const next = Array.from(new Set([...tags, v]));
    const { data } = await api.post(`/photos/${id}/tags`, { tags: next });
    setP({ ...p, manual_tags: data.manual_tags });
    setTagInput("");
  };
  const removeTag = async (t) => {
    const next = tags.filter((x) => x !== t);
    const { data } = await api.post(`/photos/${id}/tags`, { tags: next });
    setP({ ...p, manual_tags: data.manual_tags });
  };

  const saveEdit = async () => {
    const body = {};
    if (edit.title) body.title = edit.title;
    if (edit.author) body.author = edit.author;
    if (edit.photographer) body.photographer = edit.photographer;
    if (edit.photographer_url) body.photographer_url = edit.photographer_url;
    if (edit.caption) body.caption = edit.caption;
    if (edit.description) body.description = edit.description;
    if (edit.primary_domain) body.primary_domain = edit.primary_domain;
    if (edit.landmark_name) body.landmark_name = edit.landmark_name;
    if (edit.city || edit.country) body.location = { city: edit.city || loc.city, country: edit.country || loc.country };
    const { data } = await api.post(`/photos/${id}/edit`, body);
    setP(data); setEdit({ title: "", author: "", photographer: "", photographer_url: "", caption: "", description: "", primary_domain: "", landmark_name: "", city: "", country: "" });
    toast.success("Saved (source: User)");
  };
  const addNature = async () => {
    if (!nat.common_name) return;
    const { data } = await api.post(`/photos/${id}/edit`, { add_nature: nat });
    setP(data); setNat({ common_name: "", scientific_name: "", type: "plant" });
    toast.success("Species tag added");
  };
  const addCategory = async () => {
    if (!cat.category) return;
    const { data } = await api.post(`/photos/${id}/edit`, { add_category: cat });
    setP(data); setCat({ category: "", subcategory: "" });
    toast.success("Category added");
  };

  const setPrivacy = async (v) => { await api.post(`/photos/${id}/privacy`, { privacy: v }); setP({ ...p, privacy: v }); toast.success(`Privacy → ${v}`); };
  const setStatus = async (v) => { await api.post(`/photos/${id}/status`, { status: v }); setP({ ...p, status: v }); toast.success(`Status → ${v}`); };
  const reanalyze = async () => { await api.post(`/photos/${id}/reanalyze`); setP({ ...p, status: "analyzing" }); toast.info("Re-analysis queued"); };
  const del = async () => { await api.delete(`/photos/${id}`); toast.success("Photo deleted"); nav("/library"); };

  return (
    <div className="mx-auto max-w-[1400px]">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-zinc-900">
        <ArrowLeft size={16} weight="bold" /> Back
      </button>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Image */}
        <div>
          <div className="relative overflow-hidden rounded-md border border-border bg-zinc-900">
            {p.media_type === "video" ? (
              <video data-testid="photo-video" src={videoUrl(id)} poster={imageUrl(id, "display")}
                controls playsInline className="max-h-[70vh] w-full object-contain bg-black" />
            ) : (
              <>
                <img src={`${imageUrl(id)}${imgVer ? `?v=${imgVer}` : ""}`} alt={p.caption} className="max-h-[70vh] w-full object-contain" />
                <button type="button" data-testid="crop-photo-btn" onClick={openCrop}
                  className="absolute right-2 top-2 flex items-center gap-1.5 rounded-md bg-black/60 px-2.5 py-1.5 text-xs font-bold text-white backdrop-blur-sm hover:bg-black/80">
                  <Crop size={14} weight="bold" /> Crop
                </button>
              </>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-sm capitalize">{(p.status || "").replace("_", " ")}</Badge>
            <ConfidenceBadge score={p.overall_confidence} />
            {p.primary_domain && <Badge className="rounded-sm bg-[#F27A54]">{p.primary_domain}</Badge>}
            {p.is_duplicate && <Badge variant="destructive" className="rounded-sm">Possible Duplicate</Badge>}
          </div>
          <h1 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight">
            {p.title || p.caption || (p.status === "analyzing" ? "Analyzing photograph…" : "Untitled photograph")}
          </h1>
          {p.title && p.caption && p.title !== p.caption && <p className="mt-1 text-sm text-zinc-500">{p.caption}</p>}
          {p.author && <p className="mt-1 text-xs text-muted-foreground">By {p.author}{p.source_site ? ` · via ${p.source_site}` : ""}</p>}
          {p.description
            ? <RichText html={p.description} className="mt-2 text-sm leading-relaxed text-zinc-600" />
            : ai.description && <p className="mt-2 text-sm leading-relaxed text-zinc-600">{ai.description}</p>}
        </div>

        {/* Data panel */}
        <div>
          <Tabs defaultValue="overview">
            <TabsList className="grid w-full grid-cols-4 rounded-md">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
              <TabsTrigger value="correct" data-testid="tab-correct">Correct</TabsTrigger>
              <TabsTrigger value="actions" data-testid="tab-actions">Actions</TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              <section className="rounded-md border border-border p-4">
                <div className="mb-2 flex items-center gap-2"><TextT size={16} className="text-[#F27A54]" weight="fill" />
                  <h3 className="font-display text-sm font-bold">Attribution</h3></div>
                <Row label="Title" value={p.title} />
                <Row label="Author" value={p.author} />
                <div className="flex items-start justify-between gap-4 border-b border-border py-2">
                  <span className="overline text-[10px] text-muted-foreground">Photographer</span>
                  <span className="text-right text-sm font-medium text-zinc-800">
                    {p.photographer ? (
                      p.photographer_url ? (
                        <a href={p.photographer_url} target="_blank" rel="noreferrer" data-testid="photographer-link"
                          className="text-[#F27A54] underline-offset-2 hover:underline">{p.photographer} ↗</a>
                      ) : p.photographer
                    ) : "—"}
                  </span>
                </div>
                <Row label="Submitted by" value={p.submitter} />
                <Row label="Source site" value={p.source_site} />
                <Row label="External ID" value={p.external_id} />
                <Row label="Source" value={p.source} />
              </section>

              <section className="rounded-md border border-border p-4">
                <div className="mb-2 flex items-center gap-2"><MapPin size={16} className="text-[#F27A54]" weight="fill" />
                  <h3 className="font-display text-sm font-bold">Location (Enriched)</h3></div>
                <Row label="Continent" value={loc.continent} />
                <Row label="Country" value={loc.country} />
                <Row label="State / Province" value={loc.state} />
                <Row label="District" value={loc.district} />
                <Row label="City / Town" value={loc.city} />
                <Row label="Neighborhood" value={loc.neighborhood} />
                <Row label="Road" value={loc.road} />
                <Row label="Source" value={loc.source} />
              </section>

              <section className="rounded-md border border-border p-4">
                <div className="mb-2 flex items-center gap-2"><Camera size={16} className="text-zinc-500" weight="fill" />
                  <h3 className="font-display text-sm font-bold">Original Metadata (EXIF)</h3></div>
                <Row label="Date Taken" value={orig.date_taken ? new Date(orig.date_taken).toLocaleString() : null} />
                <Row label="GPS" value={orig.gps ? `${orig.gps.lat}, ${orig.gps.lon}` : "Not embedded"} />
                <Row label="Camera" value={[orig.camera_make, orig.camera_model].filter(Boolean).join(" ") || null} />
                <Row label="Lens" value={orig.lens} />
                <Row label="Focal / Aperture" value={[orig.focal_length, orig.aperture].filter(Boolean).join(" · ") || null} />
                <Row label="Shutter / ISO" value={[orig.shutter_speed, orig.iso && `ISO ${orig.iso}`].filter(Boolean).join(" · ") || null} />
                <Row label="Dimensions" value={p.width && p.height ? `${p.width} × ${p.height}` : null} />
              </section>

              {(p.nature?.length > 0) && (
                <section className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4">
                  <div className="mb-2 flex items-center gap-2"><Leaf size={16} className="text-emerald-600" weight="fill" />
                    <h3 className="font-display text-sm font-bold">Nature ID (NaturePulse)</h3></div>
                  <div className="space-y-2">
                    {p.nature.map((n, i) => (
                      <div key={i} className="flex items-center justify-between rounded-sm bg-white px-3 py-2">
                        <div>
                          <p className="text-sm font-semibold">{n.common_name} <span className="text-xs font-normal capitalize text-muted-foreground">· {n.type}</span></p>
                          {n.scientific_name && <p className="font-mono-p text-xs italic text-emerald-700">{n.scientific_name}</p>}
                        </div>
                        <ConfidenceBadge score={n.confidence} />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {(p.categories?.length > 0) && (
                <section className="rounded-md border border-border p-4">
                  <div className="mb-2 flex items-center gap-2"><Tag size={16} className="text-[#F27A54]" weight="fill" /><h3 className="font-display text-sm font-bold">AI Categories</h3></div>
                  <div className="flex flex-wrap gap-2">
                    {p.categories.map((c, i) => (
                      <Badge key={i} variant="outline" className="rounded-sm">{c.category}{c.subcategory ? ` › ${c.subcategory}` : ""} · {c.confidence}%</Badge>
                    ))}
                  </div>
                </section>
              )}

              {(p.objects?.length > 0) && (
                <section className="rounded-md border border-border p-4">
                  <h3 className="mb-2 font-display text-sm font-bold">Objects Detected</h3>
                  <div className="flex flex-wrap gap-2">
                    {p.objects.map((o, i) => <Badge key={i} variant="secondary" className="rounded-sm capitalize">{o.object_type} · {o.confidence}%</Badge>)}
                  </div>
                </section>
              )}

              {(p.ocr?.length > 0) && (
                <section className="rounded-md border border-border p-4">
                  <div className="mb-2 flex items-center gap-2"><TextT size={16} weight="fill" className="text-zinc-500" /><h3 className="font-display text-sm font-bold">OCR Text</h3></div>
                  {p.ocr.map((o, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border py-1.5 text-sm last:border-0">
                      <span className="font-mono-p">"{o.text}"</span>
                      <span className="text-xs text-muted-foreground">{o.language} · {o.confidence}%</span>
                    </div>
                  ))}
                </section>
              )}

              {p.landmark?.name && (
                <section className="rounded-md border border-purple-200 bg-purple-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Buildings size={16} weight="fill" className="text-purple-600" />
                      <span className="font-display text-sm font-bold">{p.landmark.name}</span></div>
                    <ConfidenceBadge score={p.landmark.confidence} />
                  </div>
                </section>
              )}

              {p.people?.count > 0 && (
                <section className="rounded-md border border-border p-4">
                  <div className="flex items-center gap-2"><Users size={16} weight="fill" className="text-zinc-500" />
                    <span className="text-sm"><b>{p.people.count}</b> people · {p.people.context}{p.people.has_children ? " · includes children" : ""}</span></div>
                </section>
              )}

              <section className="rounded-md border border-border bg-zinc-50 p-4">
                <div className="mb-2 flex items-center gap-2"><Info size={15} className="text-zinc-400" /><h3 className="overline text-[10px]">Source Provenance</h3></div>
                <div className="space-y-1">
                  {Object.entries(p.provenance || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs"><span className="capitalize text-muted-foreground">{k.replace("_", " ")}</span><span className="font-mono-p">{v}</span></div>
                  ))}
                </div>
              </section>
            </TabsContent>

            {/* Tags */}
            <TabsContent value="tags" className="mt-4">
              <div className="rounded-md border border-border p-4">
                <h3 className="font-display text-sm font-bold">Manual Tags</h3>
                <p className="mb-3 text-xs text-muted-foreground">Add your own tags for identification & search. Searchable across all sites.</p>
                <div className="flex gap-2">
                  <Input data-testid="tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                    placeholder="Type a tag and press Enter" className="h-10 rounded-md" />
                  <Button data-testid="tag-add-btn" onClick={addTag} className="h-10 gap-1 rounded-md bg-[#F27A54] hover:bg-[#d9633f]"><Plus size={16} weight="bold" />Add</Button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tags.length === 0 && <p className="text-sm text-muted-foreground">No manual tags yet.</p>}
                  {tags.map((t) => (
                    <span key={t} data-testid={`tag-${t}`} className="flex items-center gap-1.5 rounded-sm border border-[#F27A54]/30 bg-blue-50 px-2.5 py-1 text-sm text-[#F27A54]">
                      {t}
                      <button onClick={() => removeTag(t)} className="text-[#F27A54]/60 hover:text-[#F27A54]"><X size={13} weight="bold" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Correct */}
            <TabsContent value="correct" className="mt-4 space-y-4">
              <div className="rounded-md border border-border p-4">
                <h3 className="mb-3 font-display text-sm font-bold">Manual Correction</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline text-[10px]">Title</label>
                      <Input data-testid="edit-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder={p.title || "Photo title"} className="mt-1 h-10 rounded-md" /></div>
                    <div><label className="overline text-[10px]">Author</label>
                      <Input data-testid="edit-author" value={edit.author} onChange={(e) => setEdit({ ...edit, author: e.target.value })} placeholder={p.author || "Author / credit"} className="mt-1 h-10 rounded-md" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline text-[10px]">Photographer</label>
                      <Input data-testid="edit-photographer" value={edit.photographer} onChange={(e) => setEdit({ ...edit, photographer: e.target.value })} placeholder={p.photographer || "Photographer name"} className="mt-1 h-10 rounded-md" /></div>
                    <div><label className="overline text-[10px]">Photographer profile URL</label>
                      <Input data-testid="edit-photographer-url" value={edit.photographer_url} onChange={(e) => setEdit({ ...edit, photographer_url: e.target.value })} placeholder={p.photographer_url || "https://socialpulse.com/u/…"} className="mt-1 h-10 rounded-md" /></div>
                  </div>
                  <div><label className="overline text-[10px]">Caption</label>
                    <Textarea data-testid="edit-caption" value={edit.caption} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} placeholder={p.caption || "Enter caption"} className="mt-1 rounded-md" /></div>
                  <div><label className="overline text-[10px]">Description</label>
                    <Textarea data-testid="edit-description" rows={4} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder={p.description || ai.description || "Enter a longer description"} className="mt-1 rounded-md" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline text-[10px]">Domain</label>
                      <Select value={edit.primary_domain} onValueChange={(v) => setEdit({ ...edit, primary_domain: v })}>
                        <SelectTrigger data-testid="edit-domain" className="mt-1 h-10 rounded-md"><SelectValue placeholder={p.primary_domain || "Select"} /></SelectTrigger>
                        <SelectContent>{DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><label className="overline text-[10px]">Landmark name</label>
                      <Input value={edit.landmark_name} onChange={(e) => setEdit({ ...edit, landmark_name: e.target.value })} placeholder={p.landmark?.name || "e.g. Fort Kochi"} className="mt-1 h-10 rounded-md" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline text-[10px]">City</label>
                      <Input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} placeholder={loc.city || "City"} className="mt-1 h-10 rounded-md" /></div>
                    <div><label className="overline text-[10px]">Country</label>
                      <Input value={edit.country} onChange={(e) => setEdit({ ...edit, country: e.target.value })} placeholder={loc.country || "Country"} className="mt-1 h-10 rounded-md" /></div>
                  </div>
                  <Button data-testid="edit-save-btn" onClick={saveEdit} className="w-full rounded-md bg-[#F27A54] hover:bg-[#d9633f]">Save Corrections</Button>
                </div>
              </div>

              <div className="rounded-md border border-emerald-200 p-4">
                <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold"><Leaf size={15} className="text-emerald-600" weight="fill" />Add Species (NaturePulse)</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={nat.common_name} onChange={(e) => setNat({ ...nat, common_name: e.target.value })} placeholder="Common name" className="h-10 rounded-md" data-testid="nature-common" />
                  <Input value={nat.scientific_name} onChange={(e) => setNat({ ...nat, scientific_name: e.target.value })} placeholder="Scientific name" className="h-10 rounded-md" />
                  <Select value={nat.type} onValueChange={(v) => setNat({ ...nat, type: v })}>
                    <SelectTrigger className="h-10 rounded-md"><SelectValue /></SelectTrigger>
                    <SelectContent>{NATURE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={addNature} className="h-10 rounded-md bg-emerald-600 hover:bg-emerald-700" data-testid="nature-add-btn">Add Species</Button>
                </div>
              </div>

              <div className="rounded-md border border-border p-4">
                <h3 className="mb-3 font-display text-sm font-bold">Add Category</h3>
                <div className="grid grid-cols-3 gap-2">
                  <Input value={cat.category} onChange={(e) => setCat({ ...cat, category: e.target.value })} placeholder="Category" className="h-10 rounded-md" />
                  <Input value={cat.subcategory} onChange={(e) => setCat({ ...cat, subcategory: e.target.value })} placeholder="Subcategory" className="h-10 rounded-md" />
                  <Button onClick={addCategory} className="h-10 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">Add</Button>
                </div>
              </div>
            </TabsContent>

            {/* Actions */}
            <TabsContent value="actions" className="mt-4 space-y-4">
              <div className="rounded-md border border-border p-4">
                <label className="overline text-[10px]">Privacy Level</label>
                <Select value={p.privacy} onValueChange={setPrivacy}>
                  <SelectTrigger data-testid="privacy-select" className="mt-1 h-10 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIVACY_LEVELS.map((pl) => <SelectItem key={pl} value={pl}>{pl}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-2 text-xs text-muted-foreground">Only <b>public / community / website</b> + <b>approved/published</b> photos appear in the cross-site API.</p>
              </div>

              <div className="rounded-md border border-border p-4">
                <label className="overline text-[10px]">Publishing Workflow</label>
                <p className="mb-2 mt-1 text-xs text-muted-foreground">Accept the AI result as-is, or edit fields in the Correct tab first, then publish.</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  <Button data-testid="accept-btn" onClick={() => setStatus("approved")} className="gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700"><CheckCircle size={15} weight="fill" />Accept AI Result</Button>
                  <Button data-testid="approve-btn" onClick={() => setStatus("approved")} variant="outline" className="rounded-md">Mark Approved</Button>
                  <Button data-testid="publish-btn" onClick={() => setStatus("published")} className="gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]"><Broadcast size={15} weight="bold" />Publish</Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={reanalyze} variant="outline" className="flex-1 gap-1.5 rounded-md"><ArrowsClockwise size={15} weight="bold" />Re-analyze</Button>
                <Button onClick={del} variant="outline" data-testid="delete-btn" className="flex-1 gap-1.5 rounded-md border-red-200 text-red-600 hover:bg-red-50"><Trash size={15} weight="bold" />Delete</Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      {cropFile && <CropModal file={cropFile} onCancel={() => setCropFile(null)} onSave={saveCrop} />}
    </div>
  );
}
